import React, { useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  Button,
  Space,
  Spin,
  message,
  Checkbox,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import inventoryService, { type ItemCategory } from '@/services/inventory.service';
import { ProductGalleryPanel } from '@/components/inventory/ProductGalleryPanel';
import { ItemEngineeringDefinitionCard } from '@/components/inventory/ItemEngineeringDefinitionCard';
import { useUomUnits } from '@/hooks/useInventory';
import {
  MEASUREMENT_CATEGORY_UOM_HINT,
  MEASUREMENT_TYPES,
  MEASUREMENT_TYPE_LABELS,
  isUomCompatibleWithMeasurement,
  normalizeMeasurementType,
  type MeasurementType,
} from '@/utils/inventory/measurementQuantity';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FALLBACK_UOMS = ['PCS', 'MTR', 'SQM', 'KG', 'SET', 'JOB', 'TUB', 'EA', 'L'];

const ITEMS_QUERY_KEY = ['items'];

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

function listBasePath(pathname: string): string {
  return pathname.startsWith('/platform') ? '/platform/foundation/items' : '/scm/inventory/items';
}

interface ItemRow {
  id: string;
  item_code: string;
  item_name: string;
  item_description?: string;
  rack_number?: string | null;
  category_id?: string | null;
  item_type: string;
  base_uom: string;
  reorder_level?: number;
  reorder_quantity?: number;
  minimum_stock_level?: number;
  maximum_stock_level?: number;
  standard_cost?: number;
  standard_selling_price?: number;
  lead_time_days?: number;
  is_active?: boolean;
  inventory_account_id?: string | null;
  expense_account_id?: string | null;
  revenue_account_id?: string | null;
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  measurement_type?: string | null;
  default_width_mm?: number | string | null;
  default_height_mm?: number | string | null;
  barcode?: string | null;
  brand?: string | null;
  is_critical?: boolean;
  is_fixed_asset?: boolean;
  heat_number_mandatory?: boolean;
  mtc_required_on_receipt?: boolean;
}

function ItemAccountSelect(props: React.ComponentProps<typeof Select>) {
  const { data: accountsResponse } = useQuery({
    queryKey: ['chart-of-accounts-items-form'],
    queryFn: async () => {
      const res = await api.get<any>('/finance/chart-of-accounts');
      const body = res?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; account_code: string; account_name: string; is_group: boolean }[];
    },
  });
  const options = useMemo(() => {
    const list = Array.isArray(accountsResponse) ? accountsResponse : [];
    return list
      .filter((a) => !a.is_group)
      .map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }));
  }, [accountsResponse]);
  return (
    <Select
      placeholder="Use classification default"
      allowClear
      options={options}
      showSearch
      optionFilterProp="label"
      {...props}
    />
  );
}

const ItemFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const criticality = Form.useWatch('criticality', form);
  const measurementType = Form.useWatch('measurement_type', form) as MeasurementType | undefined;
  const baseUomWatch = Form.useWatch('base_uom', form) as string | undefined;
  const measurementUomMismatch =
    Boolean(measurementType) && !isUomCompatibleWithMeasurement(measurementType, baseUomWatch);
  const listPath = listBasePath(location.pathname);
  const isEdit = Boolean(id);

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: () => inventoryService.getAllCategories(),
  });

  const categoryOptions = useMemo(() => {
    return (categories as ItemCategory[]).map((c) => ({
      value: c.id,
      label: c.category_name,
    }));
  }, [categories]);

  const { data: uomRows = [], isPending: uomListLoading } = useUomUnits({ all: false });

  const baseUomOptions = useMemo(() => {
    if (uomRows.length > 0) {
      return uomRows.map((u) => ({
        value: u.code,
        label: `${u.code} — ${u.name}`,
      }));
    }
    return FALLBACK_UOMS.map((c) => ({ value: c, label: c }));
  }, [uomRows]);

  const { data: item, isLoading } = useQuery({
    queryKey: ['items', id],
    queryFn: async () => {
      const res = await api.get(`/inventory/items/${id}`);
      return unwrap<ItemRow>(res);
    },
    enabled: isEdit && !!id,
  });

  useEffect(() => {
    if (!isEdit || !item) return;
    form.setFieldsValue({
      item_code: item.item_code,
      item_name: item.item_name,
      item_description: item.item_description,
      rack_number: item.rack_number ?? undefined,
      category_id: item.category_id ?? undefined,
      item_type: item.item_type || 'Stock',
      base_uom: item.base_uom || 'PCS',
      reorder_level: Number(item.reorder_level) ?? 0,
      reorder_quantity: Number(item.reorder_quantity) ?? 0,
      minimum_stock_level: Number(item.minimum_stock_level) ?? 0,
      maximum_stock_level: Number(item.maximum_stock_level) ?? 0,
      standard_cost: Number(item.standard_cost) ?? 0,
      standard_selling_price: Number(item.standard_selling_price) ?? 0,
      lead_time_days: item.lead_time_days ?? 0,
      status: item.is_active !== false ? 'Active' : 'Inactive',
      criticality: item.is_critical ? 'Critical' : 'Non-critical',
      heat_number_mandatory: item.is_critical ? item.heat_number_mandatory !== false : false,
      mtc_required_on_receipt: Boolean(item.mtc_required_on_receipt),
      is_fixed_asset: Boolean(item.is_fixed_asset),
      inventory_account_id: item.inventory_account_id ?? undefined,
      expense_account_id: item.expense_account_id ?? undefined,
      revenue_account_id: item.revenue_account_id ?? undefined,
      profile_grade: item.profile_grade ?? undefined,
      profile_heat_no: item.profile_heat_no ?? undefined,
      profile_dimension: item.profile_dimension ?? undefined,
      profile_length: item.profile_length ?? undefined,
      measurement_type: normalizeMeasurementType(item.measurement_type) ?? undefined,
      default_width_mm: item.default_width_mm != null ? Number(item.default_width_mm) : undefined,
      default_height_mm: item.default_height_mm != null ? Number(item.default_height_mm) : undefined,
      barcode: item.barcode ?? undefined,
      brand: item.brand ?? undefined,
    });
    // Re-hydrate only when the loaded row identity changes — not on React Query refetch of the same id.
  }, [isEdit, item?.id]);

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      item_type: 'Stock',
      base_uom: 'PCS',
      reorder_level: 0,
      reorder_quantity: 0,
      minimum_stock_level: 0,
      maximum_stock_level: 0,
      standard_cost: 0,
      standard_selling_price: 0,
      lead_time_days: 0,
      status: 'Active',
      criticality: 'Non-critical',
      heat_number_mandatory: false,
      mtc_required_on_receipt: false,
      is_fixed_asset: false,
    });
  }, [isEdit, form]);

  const goBack = () => navigate(listPath);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Record<string, unknown> = {
        item_name: values.item_name,
        item_description: values.item_description ?? null,
        rack_number: values.rack_number?.trim() || null,
        category_id: values.category_id ?? null,
        item_type: values.item_type,
        base_uom: values.base_uom,
        reorder_level: values.reorder_level ?? 0,
        reorder_quantity: values.reorder_quantity ?? 0,
        minimum_stock_level: values.minimum_stock_level ?? 0,
        maximum_stock_level: values.maximum_stock_level ?? 0,
        standard_cost: values.standard_cost ?? 0,
        standard_selling_price: values.standard_selling_price ?? 0,
        lead_time_days: values.lead_time_days ?? 0,
        is_active: values.status === 'Active',
        is_critical: values.criticality === 'Critical',
        heat_number_mandatory:
          values.criticality === 'Critical' ? Boolean(values.heat_number_mandatory) : false,
        mtc_required_on_receipt:
          values.criticality === 'Critical' ? Boolean(values.mtc_required_on_receipt) : false,
        is_fixed_asset: Boolean(values.is_fixed_asset),
        inventory_account_id: values.inventory_account_id ?? null,
        expense_account_id: values.expense_account_id ?? null,
        revenue_account_id: values.revenue_account_id ?? null,
        profile_grade: values.profile_grade?.trim() || null,
        profile_heat_no: values.profile_heat_no?.trim() || null,
        profile_dimension: values.profile_dimension?.trim() || null,
        profile_length: values.profile_length?.trim() || null,
        measurement_type: values.measurement_type || null,
        default_width_mm: values.default_width_mm ?? null,
        default_height_mm: values.default_height_mm ?? null,
        barcode: values.barcode?.trim() || null,
        brand: values.brand?.trim() || null,
      };
      if (values.item_code) {
        payload.item_code = values.item_code;
      }
      if (isEdit && id) {
        await api.put(`/inventory/items/${id}`, payload);
        message.success('Item updated');
      } else {
        await api.post('/inventory/items', payload);
        message.success('Item created');
      }
      await queryClient.invalidateQueries({ queryKey: ITEMS_QUERY_KEY });
      navigate(listPath);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit item' : 'New item'}
        </Title>
      </Space>

      <Card>
        <Spin spinning={isEdit && isLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Item code is optional on create; the system can auto-generate if left blank.
            </Text>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="item_code" label="Item code (optional)">
                  <Input placeholder="Auto-generated if empty" disabled={isEdit} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="item_name"
                  label="Item name"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder="Item name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="category_id" label="Category">
                  <Select
                    placeholder="Select category"
                    allowClear
                    options={categoryOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="item_type"
                  label="Item type"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="Stock">Stock item</Select.Option>
                    <Select.Option value="Non-Stock">Non-stock</Select.Option>
                    <Select.Option value="Service">Service</Select.Option>
                    <Select.Option value="Subcontract">Subcontract / Job-work</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="base_uom"
                  label="Base UOM"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select unit"
                    loading={uomListLoading}
                    options={baseUomOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="reorder_level" label="Reorder level">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="reorder_quantity" label="Reorder qty">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="minimum_stock_level" label="Minimum stock">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="maximum_stock_level" label="Maximum stock">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="standard_cost" label="Standard cost (AED)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="standard_selling_price" label="Standard selling price (AED)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="lead_time_days" label="Lead time (days)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="Status" initialValue="Active">
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="Active">Active</Select.Option>
                    <Select.Option value="Inactive">Inactive</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="is_fixed_asset"
              valuePropName="checked"
              initialValue={false}
              tooltip="When enabled, posted GRN lines for this item can be used to create fixed-asset register entries (one asset per received unit, after posting)."
            >
              <Checkbox>Fixed asset — create assets from goods receipt (GRN)</Checkbox>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="criticality" label="Criticality" initialValue="Non-critical">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    onChange={(value: string) => {
                      if (value === 'Critical' && !item?.is_critical) {
                        form.setFieldsValue({ heat_number_mandatory: true, mtc_required_on_receipt: true });
                      }
                    }}
                  >
                    <Select.Option value="Non-critical">Non-critical</Select.Option>
                    <Select.Option value="Critical">Critical</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {criticality === 'Critical' && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="heat_number_mandatory"
                    valuePropName="checked"
                    initialValue={true}
                    tooltip="When enabled, GRN and stock movements require a heat number for this item."
                  >
                    <Checkbox>Require heat number on receipt and stock moves</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="mtc_required_on_receipt"
                    valuePropName="checked"
                    initialValue={true}
                    tooltip="When enabled, each GRN heat line must have an MTC document before posting."
                  >
                    <Checkbox>Require MTC on GRN receipt (per heat line)</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>
              Profile
            </Typography.Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="profile_grade" label="Grade">
                  <Input placeholder="Material grade" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="profile_heat_no" label="Heat No">
                  <Input placeholder="Heat / batch number" allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="profile_dimension" label="Dimension">
                  <Input placeholder="Dimension" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="profile_length"
                  label="Length"
                  tooltip="Default piece length (e.g. 6000mm or 6m). Pre-fills Length on PR / MR lines."
                >
                  <Input placeholder="Length" allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="measurement_type"
                  label="Measurement type"
                  tooltip="How Purchase Request, Material Request and Material Issue lines measure this item. Lines capture pieces plus dimensions (mm); Total Qty is converted into the base UOM."
                >
                  <Select
                    allowClear
                    placeholder="Quantity only"
                    options={MEASUREMENT_TYPES.map((t) => ({ value: t, label: MEASUREMENT_TYPE_LABELS[t] }))}
                  />
                </Form.Item>
              </Col>
              {(measurementType === 'LENGTH_WIDTH' || measurementType === 'LENGTH_WIDTH_HEIGHT') && (
                <Col span={8}>
                  <Form.Item name="default_width_mm" label="Default width (mm)">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                  </Form.Item>
                </Col>
              )}
              {measurementType === 'LENGTH_WIDTH_HEIGHT' && (
                <Col span={8}>
                  <Form.Item name="default_height_mm" label="Default height (mm)">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {measurementUomMismatch && measurementType && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Base UOM ${baseUomWatch || '—'} does not match ${MEASUREMENT_TYPE_LABELS[measurementType]}`}
                description={`Use ${MEASUREMENT_CATEGORY_UOM_HINT[measurementType]} so Total Qty can be calculated from the dimensions. Until then, documents use Total Qty = Pieces.`}
              />
            )}

            <Typography.Text type="secondary" strong style={{ display: 'block', marginBottom: 8 }}>
              GL accounts (optional)
            </Typography.Text>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="inventory_account_id" label="Inventory account">
                  <ItemAccountSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="expense_account_id" label="Expense account">
                  <ItemAccountSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="revenue_account_id" label="Revenue account">
                  <ItemAccountSelect />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="barcode" label="Barcode">
                  <Input placeholder="EAN / UPC / internal barcode" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="brand" label="Brand">
                  <Input placeholder="Brand name for recognition" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="item_description" label="Description">
                  <TextArea rows={3} placeholder="Description" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="rack_number" label="Rack Number">
                  <Input placeholder="e.g. A-12-03" allowClear />
                </Form.Item>
              </Col>
            </Row>

            {isEdit && id ? <ProductGalleryPanel itemId={id} /> : null}

            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Save
              </Button>
              <Button onClick={goBack}>Cancel</Button>
            </Space>
          </Form>
        </Spin>
      </Card>
      {isEdit && id && item ? (
        <ItemEngineeringDefinitionCard itemId={id} itemCode={item.item_code} itemType={item.item_type} />
      ) : null}
    </div>
  );
};

export default ItemFormPage;
